<template>
  <div :class="$style['schedule-container']">
    <mds-modal v-model="showModal" :width="'600px'" aria-label="Fee Schedule" action-required>
      <mds-section
        bold
        border="primary"
        border-position="bottom"
        :class="$style['main-schedule-section']"
      >
        <template v-slot:mds-section-title>
          <span id="title-id">{{ translate('feesHoldingSchedule') }}</span>
        </template>
        <template v-slot:mds-section-actions>
          <mds-button-container>
            <mds-button variation="icon-only" icon="remove" type="button" @click="closeModal">
              {{ translate('commonClose') }}
            </mds-button>
          </mds-button-container>
        </template>

        <p>{{ translate('feesHoldingsScheduleModalText') }}</p>
        <div v-if="portfolioOnly" :class="$style['holding-selector']">
          <mds-select v-model="selectedSecurity" label="Holding Name" placeholder="">
            <option
              v-for="security in securities"
              :key="security.securityId"
              :value="security.securityId"
              >{{ security.securityName }}</option
            >
          </mds-select>
        </div>
        <div :class="$style['schedule-content']">
          <div v-if="hasNoEntries" :class="$style['no-loads']">
            {{ translate('noFeeSchedule') }}
          </div>
          <div
            v-for="schedule in scheduleEntries"
            v-else
            :key="schedule[0]"
            :class="$style['fee-entry']"
          >
            <mds-section border="secondary">
              <template v-slot:mds-section-title>
                <span id="fee-type-title" :class="$style['fee-title']"
                  >{{ getType(schedule[0]) }}{{ getFundCode(schedule[0], schedule[1]) }}</span
                >
              </template>
              <mds-layout-grid>
                <mds-row v-if="isLOI(schedule[0])">
                  <mds-col>{{ schedule[1] }} {{ translate('loiMonths') }} </mds-col>
                </mds-row>
                <mds-row
                  v-for="item in schedule[1]"
                  v-else
                  :key="item.fromValue"
                  :class="$style['fee-row']"
                >
                  <mds-col :class="$style['fee-values']">
                    <span v-if="showCurrencySymbol(item.breakType)" :class="$style['front-unit']">
                      {{ getBreakType(item) }}
                    </span>
                    <span>{{ formatter.number(item.fromValue) }}</span>
                    <span v-if="!showCurrencySymbol(item.breakType)" :class="$style['back-unit']">
                      {{ getBreakType(item) }}</span
                    >
                    -
                    <span
                      v-if="showCurrencySymbol(item.breakType) && item.toValue"
                      :class="$style['front-unit']"
                      >{{ getBreakType(item) }}</span
                    >
                    <span v-if="item.toValue">
                      {{
                        schedule[0] === 'F'
                          ? formatter.number(item.toValue - 1)
                          : formatter.number(item.toValue)
                      }}
                    </span>
                    <span
                      v-if="!showCurrencySymbol(item.breakType) && item.toValue"
                      :class="$style['back-unit']"
                    >
                      {{ getBreakType(item) }}</span
                    >
                  </mds-col>
                  <mds-col :class="$style['fee-amount']">
                    <span v-if="item.amountType === 0" :class="$style['front-unit']">$</span>
                    <span>{{ item.amount.toFixed(4) }}</span>
                    <span v-if="item.amountType === 1" :class="$style['back-unit']">%</span>
                  </mds-col>
                </mds-row>
              </mds-layout-grid>
            </mds-section>
          </div>
        </div>
      </mds-section>
    </mds-modal>
  </div>
</template>

<script>
import MwcVueHelperMixin, { MwcConfigMixin } from 'mwc-vue-component-helper';
import MdsModal from '@mds/modal';
import MdsSection from '@mds/section';
import { MdsButton, MdsButtonContainer } from '@mds/button';
import MdsSelect from '@mds/select';
import { MdsLayoutGrid, MdsRow, MdsCol } from '@mds/layout-grid';

export default {
  name: 'fee-schedule',
  components: {
    MdsModal,
    MdsSection,
    MdsButtonContainer,
    MdsButton,
    MdsSelect,
    MdsLayoutGrid,
    MdsRow,
    MdsCol,
  },
  mixins: [MwcVueHelperMixin, MwcConfigMixin],
  props: {
    toggle: {
      type: Boolean,
      default: false,
    },
    securities: {
      type: [Array],
      default: () => [],
    },
    portfolioOnly: {
      type: Boolean,
      default: false,
    },
    feeSchedule: {
      type: [Array],
      deafult: () => [],
    },
  },
  data() {
    return {
      showModal: false,
      scheduleMap: {},
      selectedSecurity: null,
      scheduleType: {},
    };
  },
  computed: {
    hasNoEntries() {
      return (
        Boolean(this.scheduleEntries.length === 0 && this.selectedSecurity) ||
        (this.scheduleEntries.length === 0 && !this.portfolioOnly)
      );
    },
    scheduleEntries() {
      return Object.entries(this.scheduleMap);
    },
  },
  watch: {
    toggle: {
      handler(newVal) {
        this.showModal = newVal;
        if (newVal && !this.portfolioOnly && this.feeSchedule.length > 0) {
          this.mapByType(this.feeSchedule[0]);
        }
      },
    },
    selectedSecurity: {
      handler(newVal) {
        this.$set(this, 'scheduleMap', {});
        if (newVal) {
          this.getScheduleById(newVal);
        }
      },
    },
  },
  mounted() {
    this.scheduleType = {
      P: this.translate('feeScheduleTypeP'),
      V: this.translate('feeScheduleTypeV'),
      F: this.translate('feeScheduleTypeF'),
      D: this.translate('feeScheduleTypeD'),
      R: this.translate('feeSCheduleTypeR'),
      MT: this.translate('feeScheduleTypeMT'),
      L: this.translate('feeScheduleTypeL'),
      loi: this.translate('feeScheduleTypeLOI'),
    };
  },
  methods: {
    getFundCode(type, schedule) {
      if (type === 'loi' || schedule.length === 0 || !schedule[0].id) {
        return '';
      }
      return `(${schedule[0].id})`;
    },
    showCurrencySymbol(type) {
      return type === 1 || type === 2;
    },
    isLOI(type) {
      return type === 'loi';
    },
    getType(type) {
      return this.scheduleType[type] || 'Unknown fee type';
    },
    getBreakType(feeScheduleItem) {
      switch (feeScheduleItem.breakType) {
        case 1:
        case 2:
          return feeScheduleItem.currencySymbol;
        case 3:
          return this.translate('feeBreaktypeYear');
        case 4:
          return this.translate('feeBreaktypeMonth');
        case 10:
          return this.translate('feeBreaktypeDay');
        default:
          return '';
      }
    },
    mapByType(data) {
      const map = {};
      map.loi = parseInt(data.loiPeriod, 10) || 0;
      const { feeSchedules = [] } = data;
      feeSchedules.forEach(item => {
        if (!map[item.feeType]) {
          map[item.feeType] = [];
        }
        map[item.feeType].push(item);
      });
      if (map.loi === 0) {
        delete map.loi;
      }
      this.$set(this, 'scheduleMap', map);
    },
    getScheduleById(id) {
      const singleSchedule = this.feeSchedule.filter(item => item.secId === id);
      if (singleSchedule.length === 1) {
        this.mapByType(singleSchedule[0]);
      }
    },
    clearInfo() {
      this.$set(this, 'selectedSecurity', null);
      this.$set(this, 'scheduleMap', {});
    },
    closeModal() {
      this.clearInfo();
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" module>
@import '@mds/typography';
@import '@mds/constants';

::v-deep .main-schedule-section.mds-section___hypo > .mds-section__header-container___hypo {
  margin-bottom: $mds-space-2-x;
  border-bottom: solid 2px $mds-color-neutral-20;
}
.holding-selector {
  margin-bottom: $mds-space-2-x;
}
.schedule-content {
  height: 250px;
  overflow-y: auto;
  overflow-x: hidden;
}
.fee-title {
  @include mds-level-6-heading($bold: true);
  font-size: 16px;
}
.fee-entry {
  margin-bottom: $mds-space-3-x;
}
.fee-row {
  border-bottom: solid 1px $mds-color-neutral-80;
  padding: 2px 0;
  &:last-child {
    border-bottom: 0 none;
  }
}
.fee-amount {
  text-align: right;
}
.no-loads {
  font-weight: bold;
}
.front-unit {
  margin-right: -3px;
}
.back-unit {
  margin-left: -3px;
}
</style>
